// Beispieldaten (intern `is_demo`): eine Definition für Kennzeichnung, Hinweis,
// Dialog und Cache. Reine Helfer ohne Import aus helpers/index.js, das zieht
// aws-amplify (ESM) und bricht Jest. Die Selektion ist im Backend
// (partnerControl/sampleData.js) identisch.

// Vorlagen-Partner. Von 5403bdbe übernimmt generateDemoData die Beispieldaten,
// an af2dabfc hängen freigegebene Talente. Beide sperrt das Backend
// (PROTECTED_PARTNER), die UI bietet dort weder Hinzufügen noch Entfernen an.
export const SAMPLE_TEMPLATE_PARTNER_ID = '5403bdbe-d051-4295-98d0-2a7e32da9eba'
export const AIVY_DEFAULT_PARTNER_ID = 'af2dabfc-f3ff-43a1-80cd-53ebf3da38d1'

export const SAMPLE_DATA_TOOLTIP_ID = 'sample-data-tooltip'

export const SAMPLE_DATA_AVAILABILITY = {
  NONE: 'NONE',
  PRESENT: 'PRESENT',
  NO_PERMISSION: 'NO_PERMISSION',
  ONBOARDING: 'ONBOARDING',
  TEMPLATE_PARTNER: 'TEMPLATE_PARTNER'
}

// Das Backend arbeitet mit Zeitbudget und Cursor. Mehr als 10 Runden heißt,
// dass etwas hängt. Der Dialog zeigt dann den Fehler mit "Erneut versuchen".
export const MAX_REMOVE_ROUNDS = 10

export class SampleDataError extends Error {
  constructor(code, details = {}) {
    super(code)
    this.name = 'SampleDataError'
    this.code = code
    Object.assign(this, details)
  }
}

export const isSampleTemplatePartner = (partnerId) =>
  partnerId === SAMPLE_TEMPLATE_PARTNER_ID

export const isProtectedSamplePartner = (partnerId) =>
  partnerId === SAMPLE_TEMPLATE_PARTNER_ID ||
  partnerId === AIVY_DEFAULT_PARTNER_ID

// customer_dashboard_meta einer Stelle ist ein JSON-String (Discover schreibt
// JSON.stringify) oder schon ein Objekt. Kaputtes JSON zählt als leer.
export const parseCareerMeta = (meta) => {
  let value = meta

  try {
    for (let i = 0; i < 2 && typeof value === 'string'; i++) {
      value = JSON.parse(value)
    }
  } catch {
    return {}
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}

  return value
}

const SAMPLE_CAREER_ID_PREFIX = 'career_example_'

export const sampleCareerIdWithMust = (partnerId) =>
  `${SAMPLE_CAREER_ID_PREFIX}with_must_${partnerId}`

export const sampleCareerIdWithoutMust = (partnerId) =>
  `${SAMPLE_CAREER_ID_PREFIX}without_must_${partnerId}`

const SAMPLE_SPACE_FITS = ['good_fit', 'low_fit_on_must', 'mid_fit', 'low_fit']

const legacySampleCareerIds = (partnerId) => [
  sampleCareerIdWithMust(partnerId),
  sampleCareerIdWithoutMust(partnerId)
]

const legacySampleSpaceIds = (partnerId) =>
  legacySampleCareerIds(partnerId).flatMap((careerId) =>
    SAMPLE_SPACE_FITS.map((fit) => `sample_talent_example_${fit}_${careerId}`)
  )

const hasOwnFixedId = (record, buildIds) =>
  typeof record.partner_id === 'string' &&
  !!record.partner_id &&
  buildIds(record.partner_id).includes(record.id)

// Altbestand ohne Flag: generateDemoData setzt is_demo an Stellen erst seit
// 18.12.2025, an Talenten erst seit 11.02.2026, ein Backfill gibt es nicht.
// Solche Beispieldaten erkennt nur die feste ID, und nur solange das Flag
// fehlt (GraphQL liefert dann null). Die ID muss zur eigenen partner_id
// passen. Eine zur normalen Stelle gewordene Beispielstelle trägt is_demo
// false und fällt so heraus. Im Backend identisch.
const isLegacySampleCareer = (career) =>
  career.is_demo == null && hasOwnFixedId(career, legacySampleCareerIds)

const isLegacySampleSpace = (space) =>
  space.is_demo == null &&
  space.is_demo_data == null &&
  hasOwnFixedId(space, legacySampleSpaceIds)

// Discover-Vorlagenstellen tragen das Flag nur in der Meta.
export const isSampleCareer = (career) =>
  !!career &&
  (career.is_demo === true ||
    parseCareerMeta(career.customer_dashboard_meta).is_demo === true ||
    isLegacySampleCareer(career))

export const isSampleSpace = (space) =>
  !!space &&
  (space.is_demo === true ||
    space.is_demo_data === true ||
    isLegacySampleSpace(space))

// Von generateDemoData angelegt (Flag oder Altbestand, feste ID). Für die
// Einladen-Filter: Discover-Stellen bleiben einladbar, und eine zur normalen
// Stelle gewordene Beispielstelle (is_demo false, ID bleibt) ebenso.
export const isGeneratedSampleCareer = (career) =>
  isSampleCareer(career) &&
  typeof career.id === 'string' &&
  career.id.startsWith(SAMPLE_CAREER_ID_PREFIX)

// Fixture-Semantik: Beispieltalente zeigen Scores aus globalen Fixtures, für
// sie gibt es keine klassischen PDFs. Nicht für Kennzeichnung oder Löschen.
export const isFixtureSampleSpaceId = (spaceId) =>
  typeof spaceId === 'string' && spaceId.startsWith('sample_')

export const sampleGoodFitSpaceId = (partnerId) =>
  `sample_talent_example_good_fit_${sampleCareerIdWithMust(partnerId)}`

export const partitionBySample = (items, predicate) =>
  (items || []).reduce(
    (acc, item) => {
      acc[predicate(item) ? 'sample' : 'real'].push(item)
      return acc
    },
    { sample: [], real: [] }
  )

// Was den Nutzer am Entfernen hindert, unabhängig davon ob gerade Beispieldaten
// geladen sind. null heißt, er darf. Rolle vor Onboarding, sonst verspräche
// der Onboarding-Satz einer Person ohne Löschrecht, dass sie später entfernen kann.
export const getSampleDataGate = ({
  partnerId,
  canDelete,
  isDiscoverVisible,
  hasCompletedOnboarding
}) => {
  if (isProtectedSamplePartner(partnerId)) {
    return SAMPLE_DATA_AVAILABILITY.TEMPLATE_PARTNER
  }
  if (!canDelete) return SAMPLE_DATA_AVAILABILITY.NO_PERMISSION
  if (isDiscoverVisible && !hasCompletedOnboarding) {
    return SAMPLE_DATA_AVAILABILITY.ONBOARDING
  }

  return null
}

export const getSampleDataAvailability = ({ hasSampleData, ...gateArgs }) => {
  const gate = getSampleDataGate(gateArgs)

  if (gate === SAMPLE_DATA_AVAILABILITY.TEMPLATE_PARTNER) return gate
  if (!hasSampleData) return SAMPLE_DATA_AVAILABILITY.NONE

  return gate || SAMPLE_DATA_AVAILABILITY.PRESENT
}

// Stellentitel für den Dialog: höchstens `max`, der Rest als Zahl.
export const summarizeTitles = (careers, max = 3) => {
  const list = careers || []

  return {
    visible: list.slice(0, max),
    more: Math.max(0, list.length - max)
  }
}

const uniqueIds = (ids) =>
  [...new Set((ids || []).filter((id) => typeof id === 'string' && id))].sort()

// Scope für getSampleDataSummary und removeSampleData. Ohne IDs gilt alles
// (undefined), sortiert, damit der Cache-Key stabil bleibt.
export const normalizeSampleDataScope = (scope) => {
  const career_ids = uniqueIds(scope?.career_ids)
  const space_ids = uniqueIds(scope?.space_ids)

  if (!career_ids.length && !space_ids.length) return undefined

  return {
    ...(career_ids.length ? { career_ids } : {}),
    ...(space_ids.length ? { space_ids } : {})
  }
}

const parseJsonDeep = (value) => {
  let current = value
  for (let i = 0; i < 3 && typeof current === 'string'; i++) {
    current = JSON.parse(current)
  }

  return current
}

// partnerControl und helperControl liefern AWSJSON mit
// { statusCode, body: { [action]: { res } } }. Je nach Weg kommt das als
// String, als Objekt oder doppelt stringifiziert an.
export const parseControlResult = (raw, action) => {
  let parsed

  try {
    parsed = parseJsonDeep(raw)
    if (parsed && typeof parsed.body === 'string') {
      parsed = { ...parsed, body: parseJsonDeep(parsed.body) }
    }
  } catch {
    throw new SampleDataError('INVALID_RESPONSE')
  }

  const res = parsed?.body?.[action]?.res
  if (res === undefined || res === null) {
    throw new SampleDataError('INVALID_RESPONSE')
  }

  return res
}

const toCount = (value) => {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : 0
}

export const normalizeSampleDataSummary = (res) => {
  const careers = (Array.isArray(res?.careers) ? res.careers : []).map(
    (career) => ({
      ...career,
      archived: career.archived === true,
      sample_talents: toCount(career.sample_talents),
      real_talents: toCount(career.real_talents),
      action: career.action === 'KEEP' ? 'KEEP' : 'DELETE'
    })
  )
  const total = toCount(res?.talents?.total)

  return {
    blocked: res?.blocked || null,
    permissions: {
      can_remove: res?.permissions?.can_remove !== false,
      can_add: res?.permissions?.can_add !== false
    },
    careers,
    removableCareers: careers.filter(({ action }) => action === 'DELETE'),
    keptCareers: careers.filter(({ action }) => action === 'KEEP'),
    talents: {
      total,
      released: Math.min(toCount(res?.talents?.released), total)
    },
    partial: res?.partial === true,
    isEmpty: careers.length === 0 && total === 0
  }
}

// C1: hart gelöschte Spaces tauchen in keinem Delta auf. Solange der Cache
// Beispiel-Spaces enthält, lädt der Talents-Query deshalb voll statt Delta.
export const shouldFullFetchTalents = (cachedSpaces) =>
  !Array.isArray(cachedSpaces) || cachedSpaces.some(isSampleSpace)

const mergeRemoveRound = (total, round) => {
  total.removed.careers += toCount(round.removed?.careers)
  total.removed.talents += toCount(round.removed?.talents)
  total.removed_career_ids = uniqueIds([
    ...total.removed_career_ids,
    ...(round.removed_career_ids || [])
  ])
  total.removed_space_ids = uniqueIds([
    ...total.removed_space_ids,
    ...(round.removed_space_ids || [])
  ])
  total.ignored = uniqueIds([...total.ignored, ...(round.ignored || [])])

  const kept = new Map(total.kept_careers.map((career) => [career.id, career]))
  for (const career of round.kept_careers || []) kept.set(career.id, career)
  total.kept_careers = [...kept.values()]
}

const copyResult = (result) => ({
  ...result,
  removed: { ...result.removed },
  kept_careers: [...result.kept_careers]
})

// Ruft removeSampleData mit dem Cursor der Vorrunde auf, bis `done`. Das
// Backend ist idempotent, ein neuer Versuch nach Fehler setzt einfach neu an.
// `call({ cursor, round })` liefert das bereits geparste `res`.
export const runRemoveLoop = async (
  call,
  { maxRounds = MAX_REMOVE_ROUNDS } = {}
) => {
  const total = {
    done: false,
    rounds: 0,
    removed: { careers: 0, talents: 0 },
    removed_career_ids: [],
    removed_space_ids: [],
    kept_careers: [],
    ignored: []
  }
  let cursor

  while (total.rounds < maxRounds) {
    total.rounds += 1

    let res
    try {
      res = await call({ cursor, round: total.rounds })
    } catch (err) {
      // Fremde Fehler (Netzwerk, GraphQL) unverändert weiterreichen, aber mit
      // dem Stand der Vorrunden, damit Weiterleitung und Cache-Bereinigung greifen
      if (err && typeof err === 'object' && !err.result) {
        err.result = copyResult(total)
      }
      throw err
    }

    if (!res || typeof res !== 'object') {
      throw new SampleDataError('INVALID_RESPONSE', {
        result: copyResult(total)
      })
    }

    if (res.blocked) {
      throw new SampleDataError(res.blocked.reason || 'BLOCKED', {
        blocked: res.blocked,
        result: copyResult(total)
      })
    }

    mergeRemoveRound(total, res)

    if (res.done === true) {
      total.done = true
      return total
    }

    cursor = res.cursor || undefined
  }

  throw new SampleDataError('INCOMPLETE', { result: copyResult(total) })
}

const DETAIL_ROUTE = /^\/(career|talent)\/(?:new\/)?([^/]+)\/?$/

// Liegt die aktuelle Seite auf einem entfernten Objekt, wohin dann.
export const getRedirectAfterRemoval = (pathname, result) => {
  const match = DETAIL_ROUTE.exec(pathname || '')
  if (!match || !result) return null

  const [, type, rawId] = match
  let id = rawId
  try {
    id = decodeURIComponent(rawId)
  } catch {
    // ungültiges Escaping, dann bleibt die rohe ID
  }

  if (type === 'career' && (result.removed_career_ids || []).includes(id)) {
    return '/careers'
  }
  if (type === 'talent' && (result.removed_space_ids || []).includes(id)) {
    return '/talents'
  }

  return null
}

// Meldung, wenn removeSampleData außerhalb des Dialogs scheitert (Freigeben,
// Bulk-Archiv, Recover). null heißt, es wurde schon gemeldet: NOT_AUTHORIZED
// zeigt der query()-Wrapper selbst an.
export const getSampleDataErrorKey = (err) => {
  if (err?.message === 'NOT_AUTHORIZED') return null

  switch (err?.code) {
    case 'NO_PERMISSION':
      return 'sample_data.info.no_permission'
    case 'PROTECTED_PARTNER':
      // neutral, der Fehlerpfad kennt den Partner nicht
      return 'sample_data.info.protected'
    default:
      return 'sample_data.remove.error_text'
  }
}
